<script setup lang="ts">
import { useAuthStore } from '@/stores/auth'
import { useApi } from '@/composables/useApi'
import { useApiCache, CACHE_TTL } from '@/composables/useApiCache'
import { getRoleDisplayName } from '@/utils/roleUtils'
import { useToast } from '@/composables/useToast'
import { useAbility } from '@casl/vue'
import { useTour } from '@/composables/useTour'
import { dashboardTourSteps } from '@/composables/tours/dashboardTour'
import DashboardBroadcastMessages from '@/views/apps/broadcast/DashboardBroadcastMessages.vue'
import DashboardMyCampaignLeads from '@/views/apps/lead/DashboardMyCampaignLeads.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'read',
    subject: 'Dashboard',
  },
})

const authStore = useAuthStore()
const { t } = useI18n()
const { showToast } = useToast()
const ability = useAbility()

// Get user from auth store
const user = computed(() => authStore.user)

// Dashboard statistics
const stats = ref({
  users: {
    total: 0,
    online: 0,
    offline: 0,
  },
  campaigns: {
    total: 0,
    active: 0,
    inactive: 0,
    totalCallLists: 0,
    totalTeams: 0,
  },
  callLists: {
    total: 0,
    active: 0,
    inactive: 0,
    totalLeads: 0,
    totalValidLeads: 0,
    totalInvalidLeads: 0,
  },
  leads: {
    total: 0,
    male: 0,
    female: 0,
    other: 0,
    averageAge: 0,
  },
  broadcasts: {
    total: 0,
    active: 0,
    inactive: 0,
    totalReads: 0,
  },
  teams: {
    total: 0,
  },
})

const loading = ref(true)
const flushing = ref(false)
const isConfirmFlushVisible = ref(false)

// System status (store keys for translation, not translated strings)
const systemStatusKey = ref({
  statusKey: 'dashboard.checkingStatus',
  messageKey: 'dashboard.connectingToServer',
  color: 'warning',
  icon: 'tabler-clock',
})

// Computed reactive translations for system status
const systemStatus = computed(() => ({
  status: t(systemStatusKey.value.statusKey),
  message: t(systemStatusKey.value.messageKey),
  color: systemStatusKey.value.color,
  icon: systemStatusKey.value.icon,
}))

// Check if user is admin
const isAdmin = computed(() => {
  return ability.can('manage', 'all')
})

// Load all dashboard statistics
const loadDashboardStats = async () => {
  loading.value = true
  try {
    // Create cache API instances for all stats endpoints
    const usersStatsApi = useApiCache('/users/stats', CACHE_TTL.SHORT, { immediate: false })
    const campaignsStatsApi = useApiCache('/campaigns/stats', CACHE_TTL.SHORT, { immediate: false })
    const callListsStatsApi = useApiCache('/call-lists/stats', CACHE_TTL.SHORT, { immediate: false })
    const leadsStatsApi = useApiCache('/leads/stats', CACHE_TTL.SHORT, { immediate: false })
    const broadcastsStatsApi = useApiCache('/broadcast/stats', CACHE_TTL.SHORT, { immediate: false })
    const teamsStatsApi = useApiCache('/teams', CACHE_TTL.SHORT, { immediate: false })

    // Execute all requests in parallel
    await Promise.all([
      usersStatsApi.execute(),
      campaignsStatsApi.execute(),
      callListsStatsApi.execute(),
      leadsStatsApi.execute(),
      broadcastsStatsApi.execute(),
      teamsStatsApi.execute(),
    ])

    const usersData = usersStatsApi
    const campaignsData = campaignsStatsApi
    const callListsData = callListsStatsApi
    const leadsData = leadsStatsApi
    const broadcastsData = broadcastsStatsApi
    const teamsData = teamsStatsApi

    // Users stats
    if (usersData.data.value) {
      stats.value.users = {
        total: usersData.data.value.totalUsers || 0,
        online: usersData.data.value.onlineUsers || 0,
        offline: usersData.data.value.offlineUsers || 0,
      }
    }

    // Campaigns stats
    if (campaignsData.data.value) {
      stats.value.campaigns = {
        total: campaignsData.data.value.totalCampaigns || 0,
        active: campaignsData.data.value.activeCampaigns || 0,
        inactive: campaignsData.data.value.inactiveCampaigns || 0,
        totalCallLists: campaignsData.data.value.totalCallLists || 0,
        totalTeams: campaignsData.data.value.totalTeams || 0,
      }
    }

    // Call Lists stats
    if (callListsData.data.value) {
      stats.value.callLists = {
        total: callListsData.data.value.totalCallLists || 0,
        active: callListsData.data.value.activeCallLists || 0,
        inactive: callListsData.data.value.inactiveCallLists || 0,
        totalLeads: callListsData.data.value.totalLeads || 0,
        totalValidLeads: callListsData.data.value.totalValidLeads || 0,
        totalInvalidLeads: callListsData.data.value.totalInvalidLeads || 0,
      }
    }

    // Leads stats (from Lead model - production data)
    if (leadsData.data.value) {
      // Build gender stats from groupBy result
      const genderGroups = leadsData.data.value.leadsByGender || []
      const genderStats: Record<string, number> = {}
      genderGroups.forEach((group: any) => {
        genderStats[group.gender] = group._count.gender
      })

      stats.value.leads = {
        total: leadsData.data.value.totalLeads || 0,
        male: genderStats.Male || genderStats.male || 0,
        female: genderStats.Female || genderStats.female || 0,
        other: genderStats.Other || genderStats.other || 0,
        averageAge: leadsData.data.value.ageStats?._avg?.age || 0,
      }
    }

    // Broadcast stats
    if (broadcastsData.data.value) {
      stats.value.broadcasts = {
        total: broadcastsData.data.value.totalBroadcasts || 0,
        active: broadcastsData.data.value.activeBroadcasts || 0,
        inactive: broadcastsData.data.value.inactiveBroadcasts || 0,
        totalReads: broadcastsData.data.value.totalReads || 0,
      }
    }

    // Teams stats
    if (teamsData.data.value?.data)
      stats.value.teams.total = teamsData.data.value.total || 0
  }
  catch (error) {
    console.error('Failed to load dashboard statistics:', error)
  }
  finally {
    loading.value = false
  }
}

// Check system health
const checkSystemHealth = async () => {
  try {
    systemStatusKey.value = {
      statusKey: 'dashboard.checkingStatus',
      messageKey: 'dashboard.connectingToServer',
      color: 'warning',
      icon: 'tabler-clock',
    }

    const { data } = await useApi('/health')

    if (data.value?.status === 'healthy') {
      systemStatusKey.value = {
        statusKey: 'dashboard.systemHealthy',
        messageKey: 'dashboard.serverRunningNormally',
        color: 'success',
        icon: 'tabler-check',
      }
    }
    else {
      systemStatusKey.value = {
        statusKey: 'dashboard.systemIssue',
        messageKey: 'dashboard.serverHasIssues',
        color: 'error',
        icon: 'tabler-alert-circle',
      }
    }
  }
  catch (error) {
    systemStatusKey.value = {
      statusKey: 'dashboard.cannotConnect',
      messageKey: 'dashboard.cannotConnectToServer',
      color: 'error',
      icon: 'tabler-wifi-off',
    }
  }
}

// Event handlers for WebSocket events
const handleUserStatsUpdated = (event: CustomEvent) => {
  const updatedStats = event.detail
  stats.value.users = {
    total: updatedStats.totalUsers,
    online: updatedStats.onlineUsers,
    offline: updatedStats.offlineUsers,
  }
}

const handleUserStatusChanged = async () => {
  // Reload only user stats
  const { data } = await useApi('/users/stats')
  if (data.value) {
    stats.value.users = {
      total: data.value.totalUsers || 0,
      online: data.value.onlineUsers || 0,
      offline: data.value.offlineUsers || 0,
    }
  }
}

// Tour setup
const { startTour } = useTour()

// Load data on mount
onMounted(async () => {
  await Promise.all([
    loadDashboardStats(),
    checkSystemHealth(),
  ])

  // Listen for real-time user statistics updates
  window.addEventListener('user_stats_updated', handleUserStatsUpdated)
  window.addEventListener('userStatusChanged', handleUserStatusChanged)

  // Start dashboard tour (if not completed)
  startTour({
    tourName: 'dashboard',
    steps: dashboardTourSteps,
  })
})

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('user_stats_updated', handleUserStatsUpdated)
  window.removeEventListener('userStatusChanged', handleUserStatusChanged)
})

// Calculate percentages
const activePercentage = computed(() => {
  if (stats.value.users.total === 0)
    return 0

  return Math.round((stats.value.users.online / stats.value.users.total) * 100)
})

const campaignActivePercentage = computed(() => {
  if (stats.value.campaigns.total === 0)
    return 0

  return Math.round((stats.value.campaigns.active / stats.value.campaigns.total) * 100)
})

const callListActivePercentage = computed(() => {
  if (stats.value.callLists.total === 0)
    return 0

  return Math.round((stats.value.callLists.active / stats.value.callLists.total) * 100)
})

const leadValidPercentage = computed(() => {
  // Calculate percentage of valid leads in staging
  if (stats.value.callLists.totalLeads === 0)
    return 100 // No staging data = 100% valid (no invalid data)

  return Math.round((stats.value.callLists.totalValidLeads / stats.value.callLists.totalLeads) * 100)
})

// Flush cache function
const handleFlushCache = () => {
  isConfirmFlushVisible.value = true
}

const confirmFlushCache = async (confirmed: boolean) => {
  // If user cancelled, do nothing
  if (!confirmed)
    return

  flushing.value = true

  try {
    const { error } = await useApi('/health/cache/flush').post().json()

    if (!error.value) {
      showToast({
        type: 'success',
        message: t('dashboard.cacheFlushSuccess'),
      })

      // Reload dashboard stats after cache flush
      await loadDashboardStats()
    }
    else {
      showToast({
        type: 'error',
        message: t('dashboard.cacheFlushError'),
      })
    }
  }
  catch (err) {
    showToast({
      type: 'error',
      message: t('dashboard.cacheFlushError'),
    })
  }
  finally {
    flushing.value = false
  }
}
</script>

<template>
  <div>
    <!-- Welcome Section -->
    <VCard class="mb-6 overflow-visible">
      <VCardText>
        <div class="d-flex flex-wrap align-center justify-space-between gap-4">
          <div class="d-flex align-center">
            <VAvatar
              :image="user?.avatar"
              size="64"
              class="me-4"
            />
            <div>
              <h3 class="text-h4 mb-1">
                {{ t('dashboard.hello') }}, {{ user?.firstName }} {{ user?.lastName }}! 👋
              </h3>
              <p class="text-body-1 text-medium-emphasis mb-1">
                {{ user?.email }}
              </p>
              <VChip
                :color="getRoleDisplayName(user?.role) === 'Admin' ? 'error' : getRoleDisplayName(user?.role) === 'Manager' ? 'warning' : 'info'"
                size="small"
                variant="tonal"
              >
                <VIcon
                  icon="tabler-shield-check"
                  size="16"
                  class="me-1"
                />
                {{ getRoleDisplayName(user?.role) }}
              </VChip>
            </div>
          </div>

          <!-- System Status -->
          <div class="d-flex flex-column gap-3">
            <VCard
              variant="tonal"
              :color="systemStatus.color"
              class="pa-4"
            >
              <div class="d-flex align-center">
                <VAvatar
                  :color="systemStatus.color"
                  variant="tonal"
                  size="40"
                  class="me-3"
                >
                  <VIcon :icon="systemStatus.icon" />
                </VAvatar>
                <div>
                  <div class="text-subtitle-2 font-weight-medium">
                    {{ systemStatus.status }}
                  </div>
                  <div class="text-caption">
                    {{ systemStatus.message }}
                  </div>
                </div>
              </div>
            </VCard>

            <!-- Flush Cache Button (Admin Only) -->
            <VBtn
              v-if="isAdmin"
              color="warning"
              variant="tonal"
              :loading="flushing"
              :disabled="flushing"
              @click="handleFlushCache"
            >
              <VIcon
                icon="tabler-trash"
                class="me-2"
              />
              {{ t('dashboard.flushCache') }}
            </VBtn>
          </div>
        </div>
      </VCardText>
    </VCard>

    <VRow
      v-if="loading"
      class="mb-6"
    >
      <VCol
        v-for="i in 8"
        :key="i"
        cols="12"
        sm="6"
        md="3"
      >
        <VSkeletonLoader type="card" />
      </VCol>
    </VRow>

    <!-- Statistics Section -->
    <div v-else>
      <!-- Row 1: Overview Stats -->
      <VRow class="mb-6">
        <!-- Total Users -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard data-tour="stats-card-users">
            <VCardText>
              <div class="d-flex align-center justify-space-between mb-3">
                <VAvatar
                  color="primary"
                  variant="tonal"
                  size="40"
                >
                  <VIcon
                    icon="tabler-users"
                    size="24"
                  />
                </VAvatar>
                <VChip
                  :color="activePercentage >= 50 ? 'success' : 'warning'"
                  size="small"
                  variant="tonal"
                >
                  {{ activePercentage }}% {{ t('dashboard.online') }}
                </VChip>
              </div>
              <h3 class="text-h3 mb-1">
                {{ stats.users.total }}
              </h3>
              <p class="text-body-2 text-medium-emphasis mb-2">
                {{ t('dashboard.totalUsers') }}
              </p>
              <div class="d-flex align-center text-caption gap-3">
                <span class="text-success">
                  <VIcon
                    icon="tabler-circle-filled"
                    size="8"
                    class="me-1"
                  />
                  {{ stats.users.online }} {{ t('dashboard.online') }}
                </span>
                <span class="text-error">
                  <VIcon
                    icon="tabler-circle-filled"
                    size="8"
                    class="me-1"
                  />
                  {{ stats.users.offline }} {{ t('dashboard.offline') }}
                </span>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Total Campaigns -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard data-tour="stats-card-campaigns">
            <VCardText>
              <div class="d-flex align-center justify-space-between mb-3">
                <VAvatar
                  color="success"
                  variant="tonal"
                  size="40"
                >
                  <VIcon
                    icon="tabler-speakerphone"
                    size="24"
                  />
                </VAvatar>
                <VChip
                  :color="campaignActivePercentage >= 50 ? 'success' : 'warning'"
                  size="small"
                  variant="tonal"
                >
                  {{ campaignActivePercentage }}% {{ t('dashboard.active') }}
                </VChip>
              </div>
              <h3 class="text-h3 mb-1">
                {{ stats.campaigns.total }}
              </h3>
              <p class="text-body-2 text-medium-emphasis mb-2">
                {{ t('dashboard.totalCampaigns') }}
              </p>
              <div class="d-flex align-center text-caption gap-3">
                <span class="text-success">
                  {{ stats.campaigns.active }} {{ t('dashboard.active') }}
                </span>
                <span class="text-error">
                  {{ stats.campaigns.inactive }} {{ t('dashboard.inactive') }}
                </span>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Total Call Lists -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText>
              <div class="d-flex align-center justify-space-between mb-3">
                <VAvatar
                  color="info"
                  variant="tonal"
                  size="40"
                >
                  <VIcon
                    icon="tabler-phone-call"
                    size="24"
                  />
                </VAvatar>
                <VChip
                  :color="callListActivePercentage >= 50 ? 'success' : 'warning'"
                  size="small"
                  variant="tonal"
                >
                  {{ callListActivePercentage }}% {{ t('dashboard.active') }}
                </VChip>
              </div>
              <h3 class="text-h3 mb-1">
                {{ stats.callLists.total }}
              </h3>
              <p class="text-body-2 text-medium-emphasis mb-2">
                {{ t('dashboard.totalCallLists') }}
              </p>
              <div class="d-flex align-center text-caption gap-3">
                <span class="text-success">
                  {{ stats.callLists.active }} {{ t('dashboard.active') }}
                </span>
                <span class="text-error">
                  {{ stats.callLists.inactive }} {{ t('dashboard.inactive') }}
                </span>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Total Leads -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard data-tour="stats-card-leads">
            <VCardText>
              <div class="d-flex align-center justify-space-between mb-3">
                <VAvatar
                  color="warning"
                  variant="tonal"
                  size="40"
                >
                  <VIcon
                    icon="tabler-users-group"
                    size="24"
                  />
                </VAvatar>
                <VChip
                  :color="leadValidPercentage >= 90 ? 'success' : 'warning'"
                  size="small"
                  variant="tonal"
                >
                  {{ leadValidPercentage }}% {{ t('dashboard.valid') }}
                </VChip>
              </div>
              <h3 class="text-h3 mb-1">
                {{ stats.leads.total.toLocaleString() }}
              </h3>
              <p class="text-body-2 text-medium-emphasis mb-2">
                {{ t('dashboard.totalLeads') }}
              </p>
              <div class="d-flex align-center text-caption gap-3">
                <span class="text-success">
                  <VIcon icon="tabler-user-check" size="14" class="me-1" />
                  {{ stats.callLists.totalValidLeads.toLocaleString() }} {{ t('dashboard.validInStaging') }}
                </span>
                <span class="text-error">
                  <VIcon icon="tabler-user-x" size="14" class="me-1" />
                  {{ stats.callLists.totalInvalidLeads.toLocaleString() }} {{ t('dashboard.invalidInStaging') }}
                </span>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>

      <!-- Row 2: Additional Stats -->
      <VRow class="mb-6">
        <!-- Teams -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText class="d-flex align-center">
              <VAvatar
                color="secondary"
                variant="tonal"
                size="40"
                class="me-3"
              >
                <VIcon
                  icon="tabler-building"
                  size="24"
                />
              </VAvatar>
              <div>
                <h4 class="text-h4 mb-1">
                  {{ stats.teams.total }}
                </h4>
                <p class="text-body-2 text-medium-emphasis mb-0">
                  {{ t('dashboard.totalTeams') }}
                </p>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Broadcast Messages -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText class="d-flex align-center">
              <VAvatar
                color="error"
                variant="tonal"
                size="40"
                class="me-3"
              >
                <VIcon
                  icon="tabler-broadcast"
                  size="24"
                />
              </VAvatar>
              <div>
                <h4 class="text-h4 mb-1">
                  {{ stats.broadcasts.total }}
                </h4>
                <p class="text-body-2 text-medium-emphasis mb-0">
                  {{ t('dashboard.totalBroadcasts') }}
                </p>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Lead Gender Distribution -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText>
              <div class="d-flex align-center mb-3">
                <VAvatar
                  color="primary"
                  variant="tonal"
                  size="40"
                  class="me-3"
                >
                  <VIcon
                    icon="tabler-gender-bigender"
                    size="24"
                  />
                </VAvatar>
                <div>
                  <h5 class="text-h6 mb-1">
                    {{ t('dashboard.genderDistribution') }}
                  </h5>
                  <p class="text-caption text-medium-emphasis mb-0">
                    {{ t('dashboard.leadDemographics') }}
                  </p>
                </div>
              </div>
              <div class="d-flex justify-space-between text-caption">
                <span class="text-info">{{ t('dashboard.male') }}: {{ stats.leads.male.toLocaleString() }}</span>
                <span class="text-error">{{ t('dashboard.female') }}: {{ stats.leads.female.toLocaleString() }}</span>
              </div>
            </VCardText>
          </VCard>
        </VCol>

        <!-- Average Age -->
        <VCol
          cols="12"
          sm="6"
          md="3"
        >
          <VCard>
            <VCardText class="d-flex align-center">
              <VAvatar
                color="success"
                variant="tonal"
                size="40"
                class="me-3"
              >
                <VIcon
                  icon="tabler-cake"
                  size="24"
                />
              </VAvatar>
              <div>
                <h4 class="text-h4 mb-1">
                  {{ stats.leads.averageAge.toFixed(1) }} {{ t('dashboard.years') }}
                </h4>
                <p class="text-body-2 text-medium-emphasis mb-0">
                  {{ t('dashboard.averageAge') }}
                </p>
              </div>
            </VCardText>
          </VCard>
        </VCol>
      </VRow>
    </div>

    <!-- My Campaign Leads & Broadcast Messages Section -->
    <VRow>
      <!-- My Campaign Leads -->
      <VCol
        cols="12"
        md="6"
      >
        <DashboardMyCampaignLeads />
      </VCol>

      <!-- Broadcast Messages -->
      <VCol
        cols="12"
        md="6"
      >
        <DashboardBroadcastMessages />
      </VCol>
    </VRow>

    <!-- Confirm Flush Cache Dialog -->
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmFlushVisible"
      :confirmation-question="t('dashboard.confirmFlushCache')"
      :confirm-title="t('common.confirm')"
      :confirm-msg="t('dashboard.flushingCache')"
      :cancel-title="t('common.cancel')"
      :cancel-msg="t('common.cancelled')"
      @confirm="confirmFlushCache"
    />
  </div>
</template>
